import { useState, useEffect, useCallback } from 'react';

export interface UserLocation {
  city: string;
  state: string;
  lat: number;
  lng: number;
  street?: string;
  houseNumber?: string;
  neighbourhood?: string;
  suburb?: string;
  district?: string;
  postcode?: string;
  fullAddress?: string;
}

// Store availability by major Indian cities
const STORE_AVAILABILITY: Record<string, string[]> = {
  // Metro cities - most stores available
  mumbai: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  delhi: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  'new delhi': ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  bangalore: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  bengaluru: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  hyderabad: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  chennai: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  pune: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  kolkata: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  
  // Tier-2 cities - partial availability
  ahmedabad: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  jaipur: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  lucknow: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  chandigarh: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  noida: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  gurgaon: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  gurugram: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'],
  ghaziabad: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  faridabad: ['bigbasket', 'blinkit', 'zepto', 'instamart', 'jiomart'],
  kochi: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  coimbatore: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  indore: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  nagpur: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  vadodara: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  surat: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  visakhapatnam: ['bigbasket', 'instamart', 'jiomart'],
  bhopal: ['bigbasket', 'blinkit', 'instamart', 'jiomart'],
  patna: ['bigbasket', 'blinkit', 'jiomart'],
  mysore: ['bigbasket', 'instamart', 'jiomart'],
  mysuru: ['bigbasket', 'instamart', 'jiomart'],
  thiruvananthapuram: ['bigbasket', 'instamart', 'jiomart'],
};

// Default: all stores (fallback)
const ALL_STORES = ['bigbasket', 'blinkit', 'zepto', 'instamart', 'flipkart_minutes', 'jiomart'];

export function getAvailableStores(city: string): string[] {
  const key = city.toLowerCase().trim();
  return STORE_AVAILABILITY[key] || ALL_STORES;
}

export function useUserLocation() {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableStores, setAvailableStores] = useState<string[]>(ALL_STORES);

  const detectLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 600000, // cache for 10 minutes
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using free Nominatim API with zoom=18 for max detail
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1&zoom=18`,
        { headers: { 'User-Agent': 'GroceryCompare/1.0' } }
      );
      const data = await response.json();

      const addr = data.address || {};
      const street = addr.road || addr.pedestrian || addr.footway || '';
      const houseNumber = addr.house_number || '';
      const neighbourhood = addr.neighbourhood || addr.suburb || '';
      const district = addr.city_district || addr.county || '';
      const postcode = addr.postcode || '';

      const city =
        addr.city ||
        addr.town ||
        addr.city_district ||
        addr.village ||
        addr.county ||
        'Unknown';
      const state = addr.state || '';

      // Build a readable full address
      const addressParts = [
        houseNumber,
        street,
        neighbourhood,
        district,
        city,
        postcode,
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');

      const loc: UserLocation = {
        city,
        state,
        lat: latitude,
        lng: longitude,
        street,
        houseNumber,
        neighbourhood,
        suburb: addr.suburb || '',
        district,
        postcode,
        fullAddress,
      };
      setLocation(loc);
      setAvailableStores(getAvailableStores(city));
    } catch (err: any) {
      if (err?.code === 1) {
        setError('Location access denied');
      } else {
        setError('Could not detect location');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  return { location, loading, error, availableStores, detectLocation };
}
